// Studies: the list of studies relevant to the connected user, and - once
// one is picked - that study's dashboard (formerly a separate "Accueil" tab,
// now merged in here: there is no standalone home page anymore, this view
// covers both states depending on STATE.currentStudyId).
import { patientsApi, studiesApi, sitesApi, queriesApi, deviationsApi } from "./api-client.js";
import { escapeHtml, $, $$ } from "./dom.js";
import { STATE, currentRole, canReadSite } from "./state.js";

export async function renderStudiesView(root: HTMLElement): Promise<void> {
  if (STATE.currentStudyId != null) {
    await renderStudyDashboard(root, STATE.currentStudyId);
    return;
  }
  await renderStudiesList(root);
}

async function renderStudiesList(root: HTMLElement): Promise<void> {
  root.innerHTML = `<div class="loading">Chargement…</div>`;
  // Already scoped server-side to the caller's own investigation sites (or
  // every study, for a read:"all" role) - see services/patients/studyRoutes.ts.
  let studies: any[];
  try {
    studies = await studiesApi.list();
  } catch (err) {
    root.innerHTML = `<div class="empty-state"><h3>Erreur de chargement</h3><p>${escapeHtml(err instanceof Error ? err.message : "Erreur inconnue")}</p><button class="btn btn-secondary" id="btnRetryStudies">Réessayer</button></div>`;
    $("#btnRetryStudies")!.addEventListener("click", () => void renderStudiesList(root));
    return;
  }

  if (studies.length === 0) {
    root.innerHTML = `<div class="empty-state"><div class="icon">🧪</div><h3>Aucune étude</h3><p>Aucune étude clinique n'est associée à vos sites investigateurs.</p></div>`;
    return;
  }

  // Sponsor-class (read:"all") roles see the study-wide patient total
  // already returned by GET /studies; site-scoped roles (investigator, ARC,
  // monitor, ...) should only see patients on their own investigation
  // sites, so re-derive the count per study from GET /patients?studyId=X,
  // which is already scoped server-side to the caller's own sites.
  const isGlobalRole = currentRole()?.perms.read === "all";
  const siteScopedCounts: Record<number, number> = {};
  if (!isGlobalRole) {
    await Promise.all(
      studies.map(async (s: any) => {
        try {
          siteScopedCounts[s.id] = (await patientsApi.list(undefined, s.id)).length;
        } catch {
          siteScopedCounts[s.id] = 0;
        }
      })
    );
  }

  root.innerHTML = `
    <div class="home-greeting">
      <h2>🧪 Études</h2>
      <p>Sélectionnez une étude pour accéder à ses patients.</p>
    </div>
    <div class="home-cards" id="studiesGrid">
      ${studies
        .map((s: any) => {
          const count = isGlobalRole ? s.patientCount : (siteScopedCounts[s.id] ?? 0);
          return `<div class="home-card" data-study-id="${s.id}">
            <div class="icon">🧪</div>
            <h3>${escapeHtml(s.name)}</h3>
            <p>
              <span class="badge ${s.species === "human" ? "badge-info" : "badge-success"}">${s.species === "human" ? "Humain" : "Animal"}</span>
              · ${count} patient${count === 1 ? "" : "s"}
            </p>
            <p>${escapeHtml((s.siteCodes || []).join(", ") || "—")}</p>
            ${s.description ? `<p>${escapeHtml(s.description)}</p>` : ""}
          </div>`;
        })
        .join("")}
    </div>`;

  $$<HTMLElement>("[data-study-id]", $("#studiesGrid")!).forEach((el) => {
    el.addEventListener("click", () => {
      const currentStudyId = Number(el.getAttribute("data-study-id"));
      void import("./router.js").then((m) => m.navigate({ currentStudyId }));
    });
  });
}

// Formerly renderHome() in home-view.ts. Patients are scoped server-side per
// site+study already (see patientRoutes.ts); queries/deviations aren't
// study-scoped server-side, so they're linked to a site here by joining
// their patientId against the (already site+study-scoped) patient list - a
// read:"all" role (sponsor, admin, ...) sees every site's block, a
// site-scoped role only sees their own.
async function renderStudyDashboard(root: HTMLElement, studyId: number): Promise<void> {
  root.innerHTML = `<div class="loading">Chargement…</div>`;

  let study: any;
  try {
    study = await studiesApi.get(studyId);
  } catch {
    STATE.currentStudyId = null;
    void import("./router.js").then((m) => m.syncUrl(true));
    return renderStudiesView(root);
  }

  let patients: any[], sites: any[], queries: any[], deviations: any[];
  try {
    [patients, sites, queries, deviations] = await Promise.all([
      patientsApi.list(undefined, studyId),
      sitesApi.list(),
      queriesApi.list(),
      deviationsApi.list(),
    ]);
  } catch (err) {
    root.innerHTML = `<div class="empty-state"><h3>Erreur de chargement</h3><p>${escapeHtml(err instanceof Error ? err.message : "Erreur inconnue")}</p><button class="btn btn-secondary" id="btnRetryDashboard">Réessayer</button></div>`;
    $("#btnRetryDashboard")!.addEventListener("click", () => void renderStudyDashboard(root, studyId));
    return;
  }

  const byStatus = (list: any[], status: string) => list.filter((p) => p.status === status).length;
  const patientSiteById: Record<string, string> = Object.fromEntries(patients.map((p: any) => [p.id, p.siteid]));

  const siteNameByCode = Object.fromEntries(sites.map((s: any) => [s.code, s.name]));
  const siteSummaries = (study.siteCodes || [])
    .filter((code: string) => canReadSite(code))
    .map((code: string) => {
      const sitePatients = patients.filter((p: any) => p.siteid === code);
      const siteQueries = queries.filter((q: any) => patientSiteById[q.patientId] === code);
      const siteDeviations = deviations.filter((d: any) => patientSiteById[d.patientId] === code);
      return {
        code,
        name: siteNameByCode[code] || code,
        total: sitePatients.length,
        active: byStatus(sitePatients, "ACTIVE"),
        screening: byStatus(sitePatients, "SCREENING"),
        completed: byStatus(sitePatients, "COMPLETED"),
        withdrawn: byStatus(sitePatients, "WITHDRAWAL BY SUBJECT"),
        screenFail: byStatus(sitePatients, "SCREEN FAILURE"),
        queriesOpen: siteQueries.filter((q: any) => q.status === "Ouverte").length,
        deviationsTotal: siteDeviations.length,
      };
    });

  const h = new Date().getHours();
  const greeting = h < 12 ? "Bonjour" : h < 18 ? "Bon après-midi" : "Bonsoir";
  const roleLabel = currentRole()?.label ?? STATE.user?.role ?? "";

  root.innerHTML = `
    <div class="home-greeting">
      <button class="btn btn-secondary btn-sm" id="btnBackToStudies" style="margin-bottom:10px">← Toutes les études</button>
      <h2>${greeting}, ${escapeHtml(STATE.user?.fullName || STATE.user?.username)}</h2>
      <p>${escapeHtml(study.name)} (${study.species === "human" ? "Humain" : "Animal"}) — Bienvenue dans l'eCRF (${escapeHtml(roleLabel)})</p>
    </div>

    <h3 style="margin-bottom: 12px; font-size: 16px;">${siteSummaries.length > 1 ? "Résumé par site" : "Résumé"}</h3>
    ${
      siteSummaries.length === 0
        ? `<p class="muted">Aucun site investigateur associé à cette étude sur vos accès.</p>`
        : siteSummaries
            .map(
              (s: any) => `
      <h4 style="margin-bottom: 10px; font-size: 13.5px; color: var(--text-muted);">${escapeHtml(s.name)} <span class="muted">(${escapeHtml(s.code)})</span></h4>
      <div class="home-stats" style="margin-bottom: 24px;">
        <div class="stat-card"><div class="label">Patients (total)</div><div class="value">${s.total}</div></div>
        <div class="stat-card"><div class="label">Actifs</div><div class="value" style="color: var(--success);">${s.active}</div></div>
        <div class="stat-card"><div class="label">Screening</div><div class="value" style="color: var(--accent);">${s.screening}</div></div>
        <div class="stat-card"><div class="label">Terminés</div><div class="value" style="color: #6366f1;">${s.completed}</div></div>
        <div class="stat-card"><div class="label">Sorties d'étude</div><div class="value" style="color: var(--danger);">${s.withdrawn}</div></div>
        <div class="stat-card"><div class="label">Screen Failures</div><div class="value" style="color: var(--warning);">${s.screenFail}</div></div>
        <div class="stat-card"><div class="label">Queries ouvertes</div><div class="value" style="color: var(--warning);">${s.queriesOpen}</div></div>
        <div class="stat-card"><div class="label">Déviations</div><div class="value" style="color: var(--danger);">${s.deviationsTotal}</div></div>
      </div>`
            )
            .join("")
    }`;

  $("#btnBackToStudies")!.addEventListener("click", () => {
    void import("./router.js").then((m) => m.navigate({ currentStudyId: null }));
  });
}
